/**
 * AxiomsPanel — the four MIT axioms A1·A2·A3·A4 as the FORBID predicate.
 *
 * Renders the four-axiom forbidden-composite predicate from axioms.json: the
 * one operator-supplied, violable governance choice (A1, the authority subgroup
 * H ⊆ GL(n,ℂ)) and the three structural axioms (A2 spectral separation, A3
 * temporal flow, A4 Jacobi resource accounting) that hold for every gl(n,ℂ)
 * generator by construction — extending RD-65's "Key distinction" framing.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { MathInline, tex } from "@csk/demo-ui";
import axiomsData from "../data/axioms.json" with { type: "json" };
function classCaption(cls: string): string {
  if (cls === "operator-supplied") return "operator-supplied · violable";
  if (cls === "built-in") return "built in over GL(n,ℂ)";
  return "structural over GL(n,ℂ) (T3 carrier)";
}
function classBadgeLabel(cls: string): string {
  if (cls === "operator-supplied") return "governance choice · violable";
  if (cls === "built-in") return "structural over ℂ";
  return "structural · not violable";
}
function classBadgeTitle(cls: string): string {
  if (cls === "operator-supplied")
    return "the operator supplies this — the sole violable predicate";
  if (cls === "built-in")
    return "holds for every generator over ℂ — not supplied, not violable";
  return "holds for every gl(n,ℂ) generator by construction — not violable";
}
export function AxiomsPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The MIT Axioms — the FORBID Predicate"
      subtitle="A generator survives distillation iff it complies with A1 — the sole violable predicate. A1 is the operator's choice (exp(X) ∈ H); A2 (spectral separation) is built in over GL(n,ℂ); A3 and A4 hold for every gl(n,ℂ) generator by construction."
      accentColor={panelTokens.carrier}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          The Matrix Immunity Theorem certifies all ten Neutralization Theorems
          once a generator set satisfies its axioms. Distillation uses{" "}
          <strong className="text-[var(--vid-bright)]">
            A1 membership as the forbidden-composite predicate
          </strong>
          : any generator whose group image{" "}
          <MathInline>{tex`\exp(X)`}</MathInline> lands outside the authority
          subgroup <MathInline>{tex`H`}</MathInline> is structurally
          annihilated. Every verdict is computed live on the @csk/core GL(n,ℂ)
          realization. The key distinction is{" "}
          <em>what the operator supplies and can violate</em> (A1) versus{" "}
          <em>what holds structurally for every generator</em> (A2, A3, A4).
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.PanelGrid minColumnWidth={320} gap={12}>
        {axiomsData.axioms.map((a) => (
          <PanelSurface.AccentCard
            key={a.id}
            symbol={a.id}
            title={a.name}
            eyebrow={classCaption(a.class)}
            accent={a.color}
            footer={
              <div className="flex flex-wrap gap-1.5">
                {PanelSurface.badge(
                  classBadgeLabel(a.class),
                  a.color,
                  classBadgeTitle(a.class),
                )}
                {a.violable
                  ? PanelSurface.badge(`annihilates: ${a.annihilates}`, panelTokens.violation)
                  : PanelSurface.badge(
                      "nothing to strip — structural",
                      panelTokens.accentTeal,
                      a.annihilates,
                    )}
              </div>
            }
          >
            <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--vid-muted)]">
              Constrains
            </div>
            <div className="mt-1 text-[13px] leading-[1.6] text-[var(--vid-text)]">
              {a.constrains}
            </div>

            <div className="mt-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--vid-muted)]">
              Where it comes from
            </div>
            <div className="mt-1 text-[13px] leading-[1.6] text-[var(--vid-text)]">
              {a.source}
            </div>
          </PanelSurface.AccentCard>
        ))}
      </PanelSurface.PanelGrid>

      <PanelSurface.SectionHeading>The key distinction (RD-65)</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`-- input vs annihilation scope  (violable predicate = A1 only)
A1 (authority subgroup)   supplied: ✅ operator picks H ⊆ GL(n,ℂ) · VIOLABLE
                          annihilates: generators whose exp(X) lands outside H
A2 (spectral separation)  supplied: ❌ built in over GL(n,ℂ)
                          annihilates: nothing — X = PDP⁻¹ always exists
A3 (temporal flow)        supplied: ❌ structural · exp semigroup law
                          annihilates: nothing — holds for all gl(n,ℂ) X
A4 (resource accounting)  supplied: ❌ structural · Jacobi det(exp X)=exp(tr X)
                          annihilates: nothing — residual ≈ 0 for all gl(n,ℂ) X`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.Callout
        title="A2 is built in over ℂ — why CSK embeds into GL(n,ℂ), not GL(n,ℝ)"
        tone="info"
        color={panelTokens.accentTeal}
      >
        <PanelSurface.Prose className="m-0">
          A2 asks each generator to decompose spectrally as{" "}
          <MathInline ariaLabel="X equals P D P inverse">
            {tex`X = PDP^{-1}`}
          </MathInline>{" "}
          so information domains block-diagonalize into independent eigenspaces.
          Over an algebraically closed field this decomposition{" "}
          <strong className="text-[var(--vid-bright)]">always exists</strong>,
          so every generator of{" "}
          <MathInline>{tex`\mathfrak{gl}(n,\mathbb{C})`}</MathInline> satisfies
          A2 by construction — there is no A2 violator for distillation to
          strip. This is exactly why CSK fixes the embedding target as{" "}
          <MathInline>{tex`\mathbb{C}`}</MathInline>: over{" "}
          <MathInline>{tex`\mathbb{R}`}</MathInline> a generator may have no
          real eigendecomposition, leaving A2 unprovable. A2 still contributes
          its conjunct to immunity; it simply contributes it for free.
        </PanelSurface.Prose>
      </PanelSurface.Callout>

      <PanelSurface.Callout
        title="A3 and A4 are carrier theorems — they hold for every generator"
        tone="info"
        color={panelTokens.carrier}
      >
        <PanelSurface.Prose className="m-0">
          A3 follows from the exp semigroup law{" "}
          <MathInline ariaLabel="exp of s X times exp of t X equals exp of s plus t X">
            {tex`\exp(sX)\,\exp(tX) = \exp((s+t)X)`}
          </MathInline>{" "}
          and A4 from the Jacobi master identity{" "}
          <MathInline ariaLabel="det of exp X equals exp of trace X">
            {tex`\det(\exp X) = \exp(\operatorname{tr} X)`}
          </MathInline>
          . Both hold for{" "}
          <strong className="text-[var(--vid-bright)]">every</strong> generator
          of <MathInline>{tex`\mathfrak{gl}(n,\mathbb{C})`}</MathInline> by the
          GL(n,ℂ) ring structure and the T2 pointing axiom{" "}
          <MathInline>{tex`\exp(0)=I`}</MathInline> — the operator never picks
          them, and no generator can fail them. The lab verifies this live: the
          A4 residual{" "}
          <MathInline>{tex`|\det(\exp X) - \exp(\operatorname{tr} X)|`}</MathInline>{" "}
          is zero within tolerance for every X, so A3 and A4 are{" "}
          <strong className="text-[var(--vid-bright)]">never</strong>{" "}
          distillation targets. A1 is the sole violable predicate.
        </PanelSurface.Prose>
      </PanelSurface.Callout>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "Matrix Immunity Theorem",
            path: "theory/structural/MATRIX-IMMUNITY-THEOREM.md",
          },
          {
            label: "A1–A4 axiom records",
            path: "agda/MatrixKernel/SecureGovernancePatternGL/AxiomInstantiation.agda",
          },
          {
            label: "CIT assembly (MIT target)",
            path: "agda/MatrixKernel/SecureGovernancePatternGL/MatrixImmunityDerivation.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
